/**
 * Onglet "Séances" unifié (DATA-003).
 *
 * 1. Filtres sticky : chips thème + source + statut conditionnel
 * 2. Section templates (source ≠ "coach")
 * 3. Section séances coach paginées (source ≠ "insightball")
 *
 * URL : ?seancesTheme=, ?seancesSource=, ?seancesStatus=, ?seancesPage=
 */
import { useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ChevronRight, ChevronLeft } from 'lucide-react'
import { T } from '../../theme'
import { useAuth } from '../../context/AuthContext'
import {
  useSessionList,
  useCreateSession,
  useDeleteSession,
  useDuplicateSession,
} from '../../hooks/useCoachingSessions'
import { ErrorState, SkeletonGrid, EmptyState, btnPrimary, btnGhost } from './_shared'
import SeanceCard from './SeanceCard'

const THEMES = [
  { value: '',               label: 'Tous thèmes' },
  { value: 'pressing',       label: 'Pressing' },
  { value: 'construction',   label: 'Construction' },
  { value: 'transition_off', label: 'Transition off' },
  { value: 'transition_def', label: 'Transition déf' },
  { value: 'entre_lignes',   label: 'Entre les lignes' },
  { value: 'cote_fort',      label: 'Côté fort' },
  { value: 'finition',       label: 'Finition' },
  { value: 'bloc',           label: 'Bloc' },
  { value: 'cpa_off',        label: 'CPA' },
  { value: 'physique',       label: 'Physique' },
]

const SOURCES = [
  { value: '',            label: 'Toutes' },
  { value: 'coach',       label: 'Mes séances' },
  { value: 'insightball', label: 'Insightball' },
]

const STATUS_VALUES = [
  { value: '',          label: 'Tous statuts' },
  { value: 'draft',     label: 'Brouillon' },
  { value: 'planned',   label: 'Planifiée' },
  { value: 'completed', label: 'Terminée' },
]

const PAGE_SIZE = 20


export default function SeancesTab() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()

  const theme  = params.get('seancesTheme')  || ''
  const source = params.get('seancesSource') || ''
  const status = params.get('seancesStatus') || ''
  const page   = Math.max(1, parseInt(params.get('seancesPage') || '1', 10) || 1)

  const setUrl = (next) => setParams(next, { replace: false })

  const setTheme = (v) => {
    const next = new URLSearchParams(params)
    if (v) next.set('seancesTheme', v); else next.delete('seancesTheme')
    next.delete('seancesPage')
    setUrl(next)
  }
  const setSource = (v) => {
    const next = new URLSearchParams(params)
    if (v) next.set('seancesSource', v); else next.delete('seancesSource')
    next.delete('seancesPage')
    if (v === 'insightball') next.delete('seancesStatus')
    setUrl(next)
  }
  const setStatus = (v) => {
    const next = new URLSearchParams(params)
    if (v) next.set('seancesStatus', v); else next.delete('seancesStatus')
    next.delete('seancesPage')
    setUrl(next)
  }
  const setPage = (p) => {
    const next = new URLSearchParams(params)
    next.set('seancesPage', String(p))
    setUrl(next)
  }

  const showTemplates   = source !== 'coach'
  const showCoach       = source !== 'insightball'
  const showStatusFilter = source !== 'insightball'

  // Templates : GET /coaching-sessions/?is_template=true&theme=X
  const templateFilters = useMemo(() => ({
    is_template: true,
    theme: theme || undefined,
    size: 50,
  }), [theme])

  // Séances coach : GET /coaching-sessions/?is_template=false&theme=X&status=Y&page=Z
  const coachFilters = useMemo(() => ({
    is_template: false,
    theme: theme || undefined,
    status: status || undefined,
    page, size: PAGE_SIZE,
  }), [theme, status, page])

  const {
    data: tData, loading: tLoading, error: tError, refetch: tRefetch,
  } = useSessionList(templateFilters)
  const {
    data: cData, loading: cLoading, error: cError, refetch: cRefetch,
  } = useSessionList(coachFilters)

  const { mutate: doCreate                          } = useCreateSession()
  const { mutate: doDelete                          } = useDeleteSession()
  const { mutate: doDuplicate, loading: duplicating } = useDuplicateSession()

  const [loadingId, setLoadingId] = useState(null)

  const templateItems = tData?.items || []
  const coachItems    = cData?.items  || []
  const coachTotal    = cData?.total  || 0
  const totalPages    = Math.max(1, Math.ceil(coachTotal / PAGE_SIZE))

  const handleLoad = async (session) => {
    setLoadingId(session.id)
    try {
      const created = await doCreate({
        from_template_id: session.id,
        name: session.template_name || session.name,
      })
      navigate(`/dashboard/seances/${created.id}`)
    } catch {
      setLoadingId(null)
      alert('Échec du chargement du modèle. Réessaie.')
    }
  }

  const handleDuplicate = async (session) => {
    try {
      const copy = await doDuplicate(session.id)
      navigate(`/dashboard/seances/${copy.id}`)
    } catch {
      alert('Échec de la duplication. Réessaie.')
    }
  }

  const handleDelete = async (session) => {
    if (!confirm(`Supprimer définitivement "${session.name}" ?`)) return
    try {
      await doDelete(session.id)
      cRefetch()
    } catch (e) {
      alert(e?.message || 'Échec de la suppression.')
    }
  }

  const cardProps = {
    currentUserId: user?.id,
    onLoad: handleLoad,
    onDuplicate: handleDuplicate,
    onDelete: handleDelete,
    loadingDuplicate: duplicating,
  }

  return (
    <div>
      {/* Filtres sticky */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 5,
        background: T.bg, paddingBottom: 12, marginBottom: 20,
        borderBottom: `1px solid ${T.rule}`,
      }}>
        {/* Chips thème */}
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 8 }}>
          {THEMES.map(t => (
            <button
              key={t.value || 'all'}
              type="button"
              onClick={() => setTheme(t.value)}
              style={{
                background: theme === t.value ? T.gold : T.surface,
                color: theme === t.value ? T.dark : T.muted2,
                border: `1px solid ${theme === t.value ? T.gold : T.rule}`,
                padding: '4px 10px', cursor: 'pointer',
                fontFamily: T.mono, fontSize: 9, fontWeight: 700,
                letterSpacing: '.1em', textTransform: 'uppercase',
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Source toggle + statut */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: 4 }}>
            {SOURCES.map(s => (
              <button
                key={s.value || 'all'}
                type="button"
                onClick={() => setSource(s.value)}
                style={{
                  background: source === s.value ? T.dark : T.surface,
                  color: source === s.value ? T.gold : T.ink,
                  border: `2px solid ${T.dark}`,
                  padding: '6px 12px', cursor: 'pointer',
                  fontFamily: T.mono, fontSize: 10, fontWeight: 700,
                  letterSpacing: '.1em', textTransform: 'uppercase',
                }}
              >
                {s.label}
              </button>
            ))}
          </div>
          {showStatusFilter && (
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {STATUS_VALUES.map(s => (
                <button
                  key={s.value || 'all'}
                  type="button"
                  onClick={() => setStatus(s.value)}
                  style={{
                    background: status === s.value ? T.dark : T.surface,
                    color: status === s.value ? T.gold : T.muted2,
                    border: `1px solid ${T.rule}`,
                    padding: '5px 10px', cursor: 'pointer',
                    fontFamily: T.mono, fontSize: 9, fontWeight: 700,
                    letterSpacing: '.08em', textTransform: 'uppercase',
                  }}
                >
                  {s.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* 3. Section templates */}
      {showTemplates && (
        <section style={{ marginBottom: 36 }}>
          <div style={{
            fontFamily: T.mono, fontSize: 9, letterSpacing: '.18em',
            textTransform: 'uppercase', color: T.gold, fontWeight: 700,
            marginBottom: 12,
          }}>
            Modèles Insightball
          </div>
          {tLoading && <SkeletonGrid count={4} minWidth={280} />}
          {!tLoading && tError && <ErrorState message={tError} onRetry={tRefetch} />}
          {!tLoading && !tError && templateItems.length === 0 && (
            <div style={{
              padding: '20px 18px',
              background: T.surface, border: `2px dashed ${T.rule}`,
              fontFamily: T.mono, fontSize: 11, color: T.muted2,
            }}>
              {theme
                ? `Aucun modèle pour le thème « ${THEMES.find(t => t.value === theme)?.label || theme} ».`
                : 'Aucun modèle disponible.'}
            </div>
          )}
          {!tLoading && !tError && templateItems.length > 0 && (
            <div style={{
              display: 'grid', gap: 16,
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            }}>
              {templateItems.map(s => (
                <SeanceCard key={s.id} session={s} {...cardProps} loadingCreate={loadingId === s.id} />
              ))}
            </div>
          )}
        </section>
      )}

      {/* 4. Section séances coach */}
      {showCoach && (
        <section>
          <div style={{
            fontFamily: T.mono, fontSize: 9, letterSpacing: '.18em',
            textTransform: 'uppercase', color: T.gold, fontWeight: 700,
            marginBottom: 12,
          }}>
            Mes séances
          </div>
          {cLoading && <SkeletonGrid count={4} minWidth={280} />}
          {!cLoading && cError && <ErrorState message={cError} onRetry={cRefetch} />}
          {!cLoading && !cError && coachItems.length === 0 && (
            <EmptyState
              title="Aucune séance"
              message={
                theme
                  ? `Aucune séance pour ce thème${status ? ' et ce statut' : ''}.`
                  : 'Charge un modèle Insightball ci-dessus pour démarrer ta première séance.'
              }
              ctaLabel="Voir les modèles"
              onCta={() => setSource('insightball')}
            />
          )}
          {!cLoading && !cError && coachItems.length > 0 && (
            <>
              <div style={{
                fontFamily: T.mono, fontSize: 10, color: T.muted2,
                letterSpacing: '.04em', marginBottom: 10,
              }}>
                {Math.min((page - 1) * PAGE_SIZE + 1, coachTotal)}–{Math.min(page * PAGE_SIZE, coachTotal)} sur {coachTotal}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {coachItems.map(s => (
                  <SeanceCard key={s.id} session={s} {...cardProps} loadingCreate={loadingId === s.id} />
                ))}
              </div>
              {totalPages > 1 && (
                <Pagination page={page} totalPages={totalPages} onChange={setPage} />
              )}
            </>
          )}
        </section>
      )}
    </div>
  )
}



function Pagination({ page, totalPages, onChange }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'center', alignItems: 'center',
      gap: 6, marginTop: 24,
    }}>
      <button
        type="button"
        disabled={page === 1}
        onClick={() => onChange(page - 1)}
        style={btnGhost({ disabled: page === 1 })}
      >
        <ChevronLeft size={12} />
      </button>
      <span style={{ fontFamily: T.mono, fontSize: 11, color: T.ink, padding: '0 8px' }}>
        {page} / {totalPages}
      </span>
      <button
        type="button"
        disabled={page === totalPages}
        onClick={() => onChange(page + 1)}
        style={btnGhost({ disabled: page === totalPages })}
      >
        <ChevronRight size={12} />
      </button>
    </div>
  )
}
